import { apiBase } from '@/config/runtime-config'
import type { GuestKnocked, GuestPreview, GuestStanding, MeetingMessage, MeetingNote } from './types'

/**
 * The guest client — deliberately NOT the shared `api` helper.
 *
 * ## A different credential
 *
 * The admin client attaches a staff session's bearer token. A guest holds a completely
 * different kind of token, on a different filter chain, with no authorities at all. Sharing
 * one client would mean one interceptor deciding which of two credentials to attach, and
 * getting that wrong sends a guest's token to an admin route or a staff token to a meeting
 * lobby. Two clients cannot make that mistake. (The voting client makes the same argument,
 * and it is the same argument.)
 *
 * ## The token lives in sessionStorage, not localStorage
 *
 * A meeting session belongs to one tab. Closing the tab should end it, and a shared laptop
 * in a campaign office should not hand the next person a live seat in a strategy call.
 */

const TOKEN_KEY = 'axis.meeting.guest.token'

export const guestToken = {
  get: () => {
    try {
      return sessionStorage.getItem(TOKEN_KEY)
    } catch {
      return null       // private mode, blocked storage — treat as not knocked
    }
  },
  set: (token: string) => {
    try {
      sessionStorage.setItem(TOKEN_KEY, token)
    } catch { /* the session simply will not survive a reload */ }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY)
    } catch { /* ignore */ }
  },
}

export class GuestApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = guestToken.get()
  const response = await fetch(`${apiBase}/meeting/guest${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  })
  if (response.status === 401) {
    // The session was revoked — which is what being removed from a meeting does — or it
    // expired. Either way the token in this tab is worthless now.
    guestToken.clear()
    throw new GuestApiError(401, 'This meeting session has ended. Open the link again.')
  }
  if (!response.ok) {
    let detail = 'Something went wrong. Please try again.'
    try {
      const body = await response.json()
      detail = body.detail ?? body.title ?? detail
    } catch { /* a non-JSON error body; keep the generic sentence */ }
    throw new GuestApiError(response.status, detail)
  }
  return response.status === 204 ? (undefined as T) : response.json() as Promise<T>
}

/**
 * Is this link real, and is the meeting open?
 *
 * Deliberately thin, and every refusal is identical — a link that never existed, one that was
 * revoked and one that expired all answer the same way. Do not try to tell them apart in the
 * interface; the server will not help you and should not.
 */
export const previewMeeting = (token: string) =>
  call<GuestPreview>(`/preview?token=${encodeURIComponent(token)}`)

/** Join the queue under a name. The response carries the session token for this tab. */
export const knock = async (token: string, displayName: string) => {
  const knocked = await call<GuestKnocked>('/knock', {
    method: 'POST',
    body: JSON.stringify({ token, displayName }),
  })
  guestToken.set(knocked.sessionToken)
  return knocked
}

/** Polled from the lobby. Carries the grant in the first answer after the host admits you. */
export const guestStanding = () => call<GuestStanding>('/standing')

export const guestLeave = async () => {
  try {
    await call<void>('/leave', { method: 'POST' })
  } finally {
    guestToken.clear()
  }
}

/** The same chat everybody in the meeting reads, through the guest's own credential. */
export const guestMessages = (since?: string) =>
  call<MeetingMessage[]>(`/messages${since ? `?since=${encodeURIComponent(since)}` : ''}`)

export const guestPostMessage = (body: string) =>
  call<MeetingMessage>('/messages', { method: 'POST', body: JSON.stringify({ body }) })

/**
 * A guest's own hand.
 *
 * Answers 204 and not a roster. The staff route hands back the queue because a chair needs
 * to see it; the list of who is in a private meeting is not something a public link should
 * hand out, so this page tracks its own hand and shows nobody else's.
 */
export const guestRaiseHand = (raised: boolean) =>
  call<void>('/hand', { method: 'POST', body: JSON.stringify({ raised }) })

/** A guest's notes are a guest's own — the chair cannot read them either. */
export const guestNote = () => call<MeetingNote>('/note')

export const guestSaveNote = (body: string) =>
  call<MeetingNote>('/note', { method: 'PUT', body: JSON.stringify({ body }) })
